import { useFiltersStore } from '#stores/filters.store';
import { useUserStore } from '#stores/user.store';
import {
  FILTER_NAME_TO_KPI_FILTER,
  getFilterTypeAndKeyFromNodeId,
  type FiltersStateType,
  type KpiQueryFiltersType,
  type KpiScopeFilterType
} from '@carrefour-gcs/shared';
import { storeToRefs } from 'pinia';
import { computed, type ComputedRef } from 'vue';
import type { FilterNameType, FiltersTreeType, KpisPerimeterType } from '../kpisPerimeter.model';
import { useGetFilters } from './useGetFilters';
import { useGetFiltersForKpis } from './useGetFiltersForKpis';
import { useGetFiltersTree } from './useGetFiltersTree';

const FILTER_EXCLUDED_FROM_COUNT: Array<keyof FiltersStateType> = [
  'competitorFilters',
  'competitorBaseBarcodeFilters',
  'competitorBaseItemFilters',
  'competitorBrandsFilters'
];

export const countFilters = (filters: FiltersStateType) => {
  return Object.values(Object.entries(filters).filter((item) => !FILTER_EXCLUDED_FROM_COUNT.includes(item[0]))).flatMap(
    (item) => item[1]
  ).length;
};

export const useFilters = (): KpisPerimeterType['filters'] => {
  const userStore = useUserStore();
  const { defaultKpisPerimeter } = storeToRefs(userStore);

  const currentFiltersValue = useGetSelectedFilters();
  const selectedFiltersKpiQuery = useGetFiltersForKpis();
  const selectedFiltersTree = useGetFiltersTree();

  const selectedFiltersCount = computed(() => countFilters(currentFiltersValue.value));

  const { setFilter } = useFiltersStore();

  const isDefault = (currentValues: FiltersStateType = currentFiltersValue.value): boolean => {
    const areEqual = (defaultValue: unknown, currentValue: unknown): boolean => {
      if (Array.isArray(defaultValue) && Array.isArray(currentValue)) {
        if (defaultValue.length !== currentValue.length) {
          return false;
        }

        const setDefault = new Set(defaultValue);
        const setCurrent = new Set(currentValue);

        if (setDefault.size !== setCurrent.size) {
          return false;
        }

        return [...setDefault].every((val) => setCurrent.has(val));
      }

      return defaultValue === currentValue;
    };

    const keys = Object.keys(defaultKpisPerimeter.value.filters) as FilterNameType[];
    return keys.every((key) => areEqual(defaultKpisPerimeter.value.filters[key], currentValues[key]));
  };

  const generateSelectedFilters = (filters: FiltersStateType) => {
    const nullIfEmpty = (filterValues: string[] | null | undefined) => {
      if (!filterValues || filterValues.length === 0) {
        // As empty array display nothing in the url, we need to be able
        // to distinguish nothing is selected from default value.
        // Using null as value will display query string key only, allowing to
        // set correct filter values when reloading the page.
        return null;
      }

      return filterValues;
    };

    return {
      activityTypesFilters: nullIfEmpty(filters.activityTypesFilters),
      assortmentTypologyFilters: nullIfEmpty(filters.assortmentTypologyFilters),
      barcodeFilters: nullIfEmpty(filters.barcodeFilters),
      bemHoldingFilters: nullIfEmpty(filters.bemHoldingFilters),
      bemMerchandiseFilters: nullIfEmpty(filters.bemMerchandiseFilters),
      brandsFilters: nullIfEmpty(filters.brandsFilters),
      competitorFilters: nullIfEmpty(filters.competitorFilters),
      competitorBaseBarcodeFilters: nullIfEmpty(filters.competitorBaseBarcodeFilters),
      competitorBaseItemFilters: nullIfEmpty(filters.competitorBaseItemFilters),
      competitorComparisonItemFilters: nullIfEmpty(filters.competitorComparisonItemFilters),
      competitorComparisonBarcodeFilters: nullIfEmpty(filters.competitorComparisonBarcodeFilters),
      competitorBrandsFilters: nullIfEmpty(filters.competitorBrandsFilters),
      integratedFranchisedFilters: nullIfEmpty(filters.integratedFranchisedFilters),
      itemFilters: nullIfEmpty(filters.itemFilters),
      nomenclatureFilters: nullIfEmpty(filters.nomenclatureFilters),
      productTypologyFilters: nullIfEmpty(filters.productTypologyFilters),
      storesFilters: nullIfEmpty(filters.storesFilters),
      storesRegionFilters: nullIfEmpty(filters.storesRegionFilters),
      suppliersFilters: nullIfEmpty(filters.suppliersFilters),
      triptyqueFilters: nullIfEmpty(filters.triptyqueFilters),
      consostyleMicroFilters: nullIfEmpty(filters.consostyleMicroFilters),
      lifestageMicroFilters: nullIfEmpty(filters.lifestageMicroFilters),
      businessBrandFilters: nullIfEmpty(filters.businessBrandFilters),
      businessBrandReducedFilters: nullIfEmpty(filters.businessBrandReducedFilters)
    };
  };

  const updateSelectedFilters = (filters: Partial<FiltersStateType> | FiltersTreeType | Partial<FiltersTreeType>) => {
    let filtersToUpdate: Partial<FiltersStateType>;

    // Check if it's tree format (objects with nodeId items, or empty objects) or state format (arrays)
    // Tree format: values are objects where items have a nodeId property (or empty objects {})
    // State format: values are arrays of strings
    const isTreeFormat = Object.values(filters).some((value) => {
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
      }
      const items = Object.values(value);
      // Empty objects are valid tree format (no filters selected)
      // Non-empty objects must have items with nodeId property
      return items.length === 0 || items.some((item) => item && typeof item === 'object' && 'nodeId' in item);
    });

    if (isTreeFormat) {
      // Convert tree structure to filter arrays
      const convertedFilters: Partial<FiltersStateType> = {};
      Object.entries(filters as Partial<FiltersTreeType>).forEach(([key, values]) => {
        if (values !== undefined) {
          const filterKey = key as keyof FiltersStateType;
          const nodeIds = Object.values(values as Record<string, { nodeId: string; label?: string }>).map(
            (filter) => filter.nodeId
          );
          convertedFilters[filterKey] = nodeIds;
        }
      });
      filtersToUpdate = convertedFilters;
    } else {
      filtersToUpdate = filters as Partial<FiltersStateType>;
    }

    const completeFilters: FiltersStateType = {
      ...currentFiltersValue.value,
      ...filtersToUpdate
    };
    setFilter('selectedFilters', generateSelectedFilters(completeFilters));
  };

  /**
   * Build KPI filters with overrides
   * This function take a partial of filters in store to override them, and return a computed with the KPI filters format (with KPI filter keys and nodeId as values)
   * @param {ComputedRef<Partial<FiltersStateType>>} overrides filter values replacing the base ones, keyed by front filter name
   * @param {{ clearKpiKeys?: KpiScopeFilterType[] }} [options] kpi keys removed from the base before the overrides are applied, for callers that replace a whole perimeter rather than extend it
   */
  const buildKpiFiltersWithOverrides = (
    overrides: ComputedRef<Partial<FiltersStateType>>,
    { clearKpiKeys = [] }: { clearKpiKeys?: KpiScopeFilterType[] } = {}
  ): ComputedRef<KpiQueryFiltersType['filters']> => {
    return computed(() => {
      const base: Partial<Record<KpiScopeFilterType, string[]>> = { ...selectedFiltersKpiQuery.value };
      clearKpiKeys.forEach((kpiKey) => {
        delete base[kpiKey];
      });

      const overrideEntries: Partial<Record<KpiScopeFilterType, string[]>> = {};

      for (const values of Object.values(overrides.value)) {
        if (!values?.length) {
          continue;
        }
        for (const nodeId of values) {
          try {
            const { filterType } = getFilterTypeAndKeyFromNodeId(nodeId);
            const kpiKey = FILTER_NAME_TO_KPI_FILTER[filterType];
            overrideEntries[kpiKey] ??= [];
            overrideEntries[kpiKey].push(nodeId);
          } catch {
            // If nodeId is invalid, we skip it (should not happen as filters should be generated from the tree)
          }
        }
      }

      return { ...base, ...overrideEntries } as KpiQueryFiltersType['filters'];
    });
  };

  return {
    currentFiltersValue,
    selectedFiltersKpiQuery,
    selectedFiltersTree,
    selectedFiltersCount,
    updateSelectedFilters,
    buildKpiFiltersWithOverrides,
    isDefault
  };
};

const useGetSelectedFilters = () => {
  const filters = useGetFilters();

  return computed<FiltersStateType>(() => ({
    nomenclatureFilters: [
      ...filters.value.sectorFilters,
      ...filters.value.departmentFilters,
      ...filters.value.groupClassFilters,
      ...filters.value.classFilters,
      ...filters.value.subClassFilters
    ],
    activityTypesFilters: filters.value.activityTypeFilters,
    storesFilters: [...filters.value.storeFilters, ...filters.value.chainTypeFilters],
    integratedFranchisedFilters: filters.value.integratedFranchisedFilters,
    triptyqueFilters: filters.value.triptyqueFilters,
    barcodeFilters: filters.value.barcodeFilters,
    itemFilters: filters.value.itemFilters,
    suppliersFilters: [...filters.value.subSupplierFilters, ...filters.value.localSupplierFilters],
    bemHoldingFilters: filters.value.bemHoldingFilters,
    bemMerchandiseFilters: filters.value.bemMerchandiseFilters,
    brandsFilters: filters.value.brandFilters,
    storesRegionFilters: filters.value.storesRegionFilters,
    assortmentTypologyFilters: filters.value.assortmentTypologyFilters,
    productTypologyFilters: filters.value.productTypologyFilters,
    competitorFilters: filters.value.competitorFilters,
    competitorBaseBarcodeFilters: filters.value.competitorBaseBarcodeFilters,
    competitorBaseItemFilters: filters.value.competitorBaseItemFilters,
    competitorComparisonItemFilters: filters.value.competitorComparisonItemFilters,
    competitorComparisonBarcodeFilters: filters.value.competitorComparisonBarcodeFilters,
    competitorBrandsFilters: filters.value.competitorBrandsFilters,
    consostyleMicroFilters: filters.value.consostyleMicroFilters,
    lifestageMicroFilters: filters.value.lifestageMicroFilters,
    businessBrandFilters: filters.value.businessBrandFilters,
    businessBrandReducedFilters: filters.value.businessBrandReducedFilters
  }));
};
